import type { DimensionValue } from 'react-native';

import { useGlobalModal } from './BottomSheetModalProvider';

import { type ReactNode, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';

export interface ContentModalRef {
    present: () => void;
    dismiss: () => void;
}

interface ContentModalProps {
    id: string;
    children: ReactNode;
    height?: DimensionValue | undefined;
}

const ContentModal = forwardRef<ContentModalRef, ContentModalProps>((props, ref) => {
    const { id, children, height } = props;
    const { registerModal, unregisterModal, showModal, hideModal } = useGlobalModal();
    const modalIdRef = useRef(id);

    // Only re-register when children change
    useEffect(() => {
        const modalId = modalIdRef.current;
        registerModal(modalId, children);
        return () => {
            unregisterModal(modalId);
        };
    }, [children, registerModal, unregisterModal]);

    useImperativeHandle(ref, () => ({
        present: () => showModal({ id, height }),
        dismiss: () => hideModal(),
    }));

    return null; // This component doesn't render anything directly
});

// Helper hook to create and use a modal ref
export const useModalRef = () => {
    return useRef<ContentModalRef>(null);
};

export { ContentModal };
